import { Link } from "react-router-dom";
import { Calendar, Clock, AlertTriangle, CheckCircle2 } from "lucide-react";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";

const EFFECTIVE_DATE = "June 10, 2026";

export default function TermsPage() {
  return (
    <>
      <NavBar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <Link to="/" className="text-sm text-gg-muted hover:text-gg-green">← Back home</Link>

        <header className="mt-6">
          <p className="uppercase tracking-[0.22em] text-xs text-gg-brown font-semibold">Legal</p>
          <h1 className="font-heading text-4xl sm:text-5xl text-[var(--gg-text)] mt-3 leading-tight">
            Terms &amp; Refund Policy
          </h1>
          <p className="text-sm text-gg-muted mt-3">Effective {EFFECTIVE_DATE}</p>
        </header>

        {/* Quick visual summary */}
        <section className="mt-10 rounded-2xl border-2 border-gg-green bg-gg-green/8 p-6 sm:p-8">
          <p className="text-xs uppercase tracking-[0.22em] text-gg-brown font-semibold mb-2">
            Refund policy — at a glance
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl text-[var(--gg-text)] leading-tight">
            Plans change. We get it — here's how cancellations work.
          </h2>
          <div className="mt-6 grid sm:grid-cols-3 gap-4">
            <RefundCard
              tone="green"
              icon={CheckCircle2}
              window="More than 2 weeks before class"
              detail="Full refund."
            />
            <RefundCard
              tone="amber"
              icon={Calendar}
              window="2 weeks to 5 days before class"
              detail="Credit toward another class (no cash refund)."
            />
            <RefundCard
              tone="red"
              icon={AlertTriangle}
              window="Less than 5 days before class"
              detail="No refund, no credit — we can no longer fill the seat."
            />
          </div>
          <p className="text-sm text-gg-muted mt-5">
            Notice means an email or phone call to Heather. <strong>The receipt timestamp on Heather's end is what counts</strong>, not the time you intended to send.
          </p>
        </section>

        <article className="mt-12 space-y-7 text-[15px] leading-relaxed text-[var(--gg-text)]/95">
          <Section title="1. What you're agreeing to">
            <p>
              By submitting a registration or 1-on-1 booking request on this site, you
              agree to these Terms and to our{" "}
              <Link className="text-gg-green hover:underline" to="/privacy">Privacy Policy</Link>.
              You also agree to the homestead liability waiver presented during checkout.
            </p>
          </Section>

          <Section title="2. Cancellation & refund policy">
            <p className="mb-2">
              Cancellations are calculated from the scheduled class start time:
            </p>
            <ul className="list-disc list-inside space-y-2">
              <li>
                <strong>More than 14 days before class</strong> — full refund. Card payments
                are refunded through Stripe within 5–10 business days. If you've already
                uploaded the ESA invoice to ClassWallet, please request a withdrawal from
                ClassWallet in parallel.
              </li>
              <li>
                <strong>14 days to 5 days before class</strong> — a credit is issued to your
                family for any other Graceful Garden Coaching class within the next twelve (12)
                months. Credits are non-transferable to other families.
              </li>
              <li>
                <strong>Fewer than 5 days before class</strong> — no refund and no credit.
                At this point ingredient, materials, and instructor commitments for your seat
                cannot be recovered and the seat cannot be re-filled.
              </li>
              <li>
                <strong>If a family simply doesn't show up</strong> — no refund and no credit.
                Heather is not responsible for missed classes when no advance notice is given.
              </li>
            </ul>
          </Section>

          <Section title="3. Cancellation by Graceful Garden Coaching">
            <p>
              If Heather has to cancel a class for any reason — illness, weather, livestock
              emergency, low enrollment — every registered family receives a <strong>full
              refund</strong> (card portion via Stripe; ESA portion via a withdrawal request you
              submit to ClassWallet) <em>or</em>, at your option, a credit toward any future
              class. We'll notify you by email and phone as soon as the call is made.
            </p>
          </Section>

          <Section title="4. ESA / ClassWallet responsibility">
            <p>
              The seven (7) day ClassWallet upload window is yours. We generate and email
              the ClassWallet-ready invoice the moment you complete checkout; uploading it
              within seven days is your responsibility. If you do not upload by then, your
              student's seat is released so another family can register.
            </p>
            <p>
              ESA funding approval is between you and the State of Arizona. We make no
              guarantee that any specific invoice will be approved by ClassWallet, although
              our invoices include Arizona Academic Standards alignment to support approval.
            </p>
          </Section>

          <Section title="5. Working homestead — assumed risks">
            <p>
              You are coming to an actively operating homestead with livestock, native insects
              and reptiles, irrigation infrastructure, cooking heat, and uneven terrain. By
              registering you accept these inherent risks on your behalf and on behalf of the
              registered student, as detailed in the liability waiver you signed during
              checkout. Every attending adult <strong>also</strong> signs a paper waiver at the
              welcome table on arrival.
            </p>
          </Section>

          <Section title="6. What you bring">
            <ul className="list-disc list-inside space-y-1">
              <li>A folding lawn or camping chair per attendee for the cooking-class portion</li>
              <li>Closed-toe shoes, sun protection, and a refillable water bottle</li>
              <li>Any allergy-relevant snacks or epinephrine your student may need</li>
            </ul>
          </Section>

          <Section title="7. Photography & promotional use">
            <p>
              Heather may take photographs and short videos of class activities for
              educational documentation and limited promotional use. Faces will not be
              paired with last names. Tell Heather at the welcome table if you'd prefer your
              student not be photographed.
            </p>
          </Section>

          <Section title="8. Designated learning areas">
            <p>
              Instruction takes place within marked areas. Participants and attending adults
              agree to remain within those areas, not to approach animals not introduced to
              them by Heather, and to follow all posted signage and reasonable instructions.
            </p>
          </Section>

          <Section title="9. Disputes">
            <p>
              These Terms are governed by the laws of the State of Arizona, with venue in
              Yavapai County. We'll always try to resolve any issue by direct conversation
              first.
            </p>
          </Section>

          <Section title="10. Updates">
            <p>
              If we materially change these Terms, the Effective Date above will update and
              we'll email every active family at least 14 days in advance.
            </p>
          </Section>
        </article>

        <p className="mt-12 text-xs text-gg-muted">
          See also: <Link className="text-gg-green hover:underline" to="/privacy">Privacy Policy</Link>.
          Questions? Email{" "}
          <a className="text-gg-green hover:underline" href="mailto:Heather@gracefulgardencoaching.com">
            Heather@gracefulgardencoaching.com
          </a>.
        </p>
      </main>
      <Footer />
    </>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-heading text-2xl text-gg-brown leading-snug mb-3">{title}</h2>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function RefundCard({ tone, icon: Icon, window, detail }) {
  const palette =
    tone === "green"
      ? "bg-gg-green/10 border-gg-green/40 text-gg-green"
      : tone === "amber"
      ? "bg-[#D9933B]/10 border-[#D9933B]/40 text-[#9b6a26]"
      : "bg-[#B24C4C]/10 border-[#B24C4C]/40 text-[#B24C4C]";
  return (
    <div className={`rounded-xl border p-5 ${palette}`}>
      <Icon size={20} />
      <p className="text-xs uppercase tracking-[0.16em] font-semibold mt-3 leading-tight">
        {window}
      </p>
      <p className="text-sm text-[var(--gg-text)] mt-2 leading-snug">{detail}</p>
    </div>
  );
}
